import { ChevronDownIcon, ChevronRightIcon } from 'lucide-react';
import { Badge, Button, Card } from '@shared/components';
import { formatInvoiceMoney } from '../utils/formatInvoiceMoney';
import { invoiceFundingBadge } from '../utils/invoiceFundingBadge';
import { invoiceMetaLine } from '../utils/invoiceMetaLine';
import { invoiceStatusLabel } from '../utils/invoiceStatusLabel';
import { invoiceCardId, isReversed } from '../utils/invoiceReversal';
import { ReverseInvoiceButton } from './ReverseInvoiceButton';
import { ReversedInvoiceDetails } from './ReversedInvoiceDetails';
import { SubmittedInvoiceLinesPanel } from './SubmittedInvoiceLinesPanel';
import type { AgreementInvoice } from '../types/agreements.types';

interface Props {
    invoice: AgreementInvoice;
    dealerName: string;
    expanded: boolean;
    onToggle: () => void;
    onReverse?: () => void;
}

const linesPanelId = (invoiceId: string): string => `submitted-invoice-${invoiceId}-lines`;
const reversalPanelId = (invoiceId: string): string => `submitted-invoice-${invoiceId}-reversal`;

const controlledPanelIds = (invoiceId: string, reversed: boolean): string =>
    reversed ? `${reversalPanelId(invoiceId)} ${linesPanelId(invoiceId)}` : linesPanelId(invoiceId);

export function SubmittedInvoiceCard({ invoice, dealerName, expanded, onToggle, onReverse }: Props) {
    const fundingBadge = invoiceFundingBadge(invoice);
    const reversed = isReversed(invoice);
    const Chevron = expanded ? ChevronDownIcon : ChevronRightIcon;
    const emphasisText = reversed ? 'text-mp-muted' : 'text-mp-foreground';

    return (
        <li id={invoiceCardId(invoice.id)}>
            <Card>
                <Button
                    type="button"
                    variant="clear"
                    size="none"
                    ariaExpanded={expanded}
                    ariaControls={expanded ? controlledPanelIds(invoice.id, reversed) : undefined}
                    onClick={onToggle}
                    className="min-h-11 w-full justify-between gap-3 px-4 py-3 text-left"
                >
                    <span className="flex min-w-0 flex-1 items-start gap-2">
                        <Chevron aria-hidden="true" className="mt-0.5 h-4 w-4 shrink-0" />
                        <span className="min-w-0 space-y-1">
                            <span className="flex flex-wrap items-center gap-2">
                                <span className={`min-w-0 break-words font-semibold ${emphasisText}`}>
                                    {invoice.invoiceNumber}
                                </span>
                                {invoice.status !== 'submitted' && (
                                    <Badge variant="outline">{invoiceStatusLabel(invoice.status)}</Badge>
                                )}
                                {fundingBadge !== null && <Badge variant="outline">{fundingBadge}</Badge>}
                            </span>
                            <span className="block text-xs text-mp-muted">{invoiceMetaLine(invoice, dealerName)}</span>
                        </span>
                    </span>
                    <span className={`shrink-0 font-semibold tabular-nums ${emphasisText}`}>
                        {formatInvoiceMoney(invoice.totalGross)}
                    </span>
                </Button>
                {expanded && reversed && <ReversedInvoiceDetails id={reversalPanelId(invoice.id)} invoice={invoice} />}
                {expanded && <SubmittedInvoiceLinesPanel id={linesPanelId(invoice.id)} lines={invoice.lines} />}
                {expanded && !reversed && onReverse && (
                    <div className="flex justify-end px-4 pb-4">
                        <ReverseInvoiceButton onClick={onReverse} />
                    </div>
                )}
            </Card>
        </li>
    );
}
